/**
 * AvatarEmbed - Unified avatar embedding component
 *
 * Provides a consistent API for embedding the avatar in builder components.
 * Supports two modes:
 * - 'iframe': Current production approach (embedded external avatar)
 * - 'native': Future Three.js rendering (in development)
 *
 * This abstraction allows gradual migration from iframe to native rendering.
 */

import React, {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import type { Sentiment, BlendshapeFrame } from '../types';
import {
  createLiveSessionContext,
  type AvatarLiveSessionKind,
  type AvatarSessionContext
} from '../session';
import { CamlinAvatar, type CamlinAvatarHandle } from './CamlinAvatar';
import {
  AVATAR_EMBED_COMMAND,
  AVATAR_EMBED_EVENT,
  type AvatarEmbedCommandEnvelope,
  isAvatarEmbedEnvelope
} from '../host/embedProtocol';
import {
  createHostContextualChatSessionContext,
  normalizeAvatarHostLiveAvailability,
  type AvatarHostLiveConfig
} from '../host';
import NativeLiveAvatar from './NativeLiveAvatar';
import { resolveLiveTransportConfig } from './liveTransport';
import { getModelByUrl, resolveModelUrlForQuality } from '../core/modelRegistry';
import { replayAvatarRuntimeConfig } from '../core/avatarRuntimeConfig';
import { useAvatarRuntimeConfig } from '../core/hooks/useAvatarRuntimeConfig';

// ============================================
// Types
// ============================================

export type AvatarEmbedMode = 'iframe' | 'native';

export interface AvatarEmbedConfig {
  /** Embed mode - 'iframe' (default) or 'native' (experimental) */
  mode?: AvatarEmbedMode;
  /** Base URL for avatar (iframe mode) */
  avatarUrl?: string;
  /** Explicit runtime API base for live/session bootstrap in native mode */
  runtimeUrl?: string;
  /** Explicit voice API base for native embeds that need direct voice services */
  voiceUrl?: string;
  /** Explicit SIP dial target for native live embeds that use WebRTC/SIP */
  liveDestination?: string;
  /** Explicit SIP websocket URL for native embeds */
  sipWebSocketUrl?: string;
  /** Project token for session context */
  projectToken?: string;
  /** Diagram ID for flow context */
  diagramId?: string;
  /** Handoff token / session ID */
  handoffToken?: string;
  /** Hide chatbot UI in avatar */
  hideChat?: boolean;
  /** Hide buttons (speech button, etc.) */
  hideButtons?: boolean;
  /** Hide the always-on AvatarHealthBadge status pip (native live mode) — for hosts that render their own health UI */
  hideHealthBadge?: boolean;
  /**
   * Hide the SDK's inline audio/mic AudioPermissionChip overlay (native
   * live mode) — for hosts that render their own labelled permission
   * affordances via `onBrowserAccessPromptChange` + `retryBrowserAccess()`.
   * Inside the Camlin Companion's minimized launcher the chip rendered as
   * an unlabelled orange icon covering the avatar's face and, because the
   * host wrapper is pointer-events:none, an unclickable one.
   */
  hideAudioPermissionChip?: boolean;
  /**
   * Warm the browser HTTP cache for the avatar model with a plain fetch()
   * before the GLTF loader requests it (native live mode; default true).
   * Hosts that remount the embed for connection recovery should pass false:
   * the warming fetch races the loader's own fetch of the same multi-MB URL
   * and Chromium can fail one of the two concurrent cache writers with
   * net::ERR_CACHE_WRITE_FAILURE, which surfaces as a model-load error.
   */
  preloadModel?: boolean;
  /** Hide SIP calling UI */
  hideSip?: boolean;
  /** Enable sentiment expressions */
  enableSentiment?: boolean;
  /** Builder mode (shows debug info) */
  builderMode?: boolean;
  /** Model URL for native mode */
  modelUrl?: string;
  /** Camera zoom level (higher = more zoomed in) */
  cameraZoom?: number;
  /** Voice ID for TTS (e.g., 'en-AU-NatashaNeural') */
  voiceId?: string;
  /** Preferred avatar model ID for iframe/live mode */
  avatarModelId?: string;
  /** Visual IVR mode */
  visualMode?: boolean;
  /** Show the canonical live-mode listen control */
  activeListen?: boolean;
  /**
   * Suppress spoken prompt playback until the host calls unlockAudio()
   * (deterministic speak gate — Companion "no speech before first open").
   */
  holdSpeechUntilUnlock?: boolean;
  /** Explicit live-session behavior contract for host-driven live mode */
  liveSessionKind?: AvatarLiveSessionKind;
  /** Deprecated compatibility flag for contextual live sessions */
  contextualLive?: boolean;
  /** Optional typed session context seed for host wrappers */
  sessionContext?: Partial<AvatarSessionContext>;
  /** Canonical opt-in live wrapper contract for host apps */
  live?: AvatarHostLiveConfig;
  /** TTS service URL (for when avatar is embedded and can't reach its own sidecar) */
  ttsUrl?: string;
  /** Show avatar model switcher UI */
  showAvatarSwitcher?: boolean;
  /** Available avatar models for switcher */
  avatarModels?: Array<{ id: string; name: string; url: string; useDefaultMaterials?: boolean }>;
  /** Show microphone button for voice input */
  showMicButton?: boolean;
  /** Use embedded textures from GLB (skip external texture loading) */
  useEmbeddedTextures?: boolean;
  /** Per-project appearance overrides applied after the model loads (eye, hair, skin, clothing colors) */
  initialAppearance?: Record<string, string | undefined>;
  /** Full avatar runtime config replayed after the model is ready. */
  initialConfig?: Record<string, unknown>;
  /** Avatar quality tier - 'hd' (full GLB ~20-30MB) or 'sd' (lowres GLB ~1-3MB) */
  avatarQuality?: 'hd' | 'sd';
  /**
   * Opt-in flag for snapshot tools (e.g. the admin Portrait Generator)
   * that need to call `canvas.toDataURL()` after the avatar is ready.
   * Forces the WebGLRenderer to use `preserveDrawingBuffer: true`, which
   * keeps the framebuffer intact between frames so single-frame captures
   * succeed instead of returning a blank PNG. Off by default — there's
   * a small per-frame copy cost in some browsers so we don't enable it
   * for normal embed/live usage.
   */
  enableCanvasCapture?: boolean;
}

export interface AvatarEmbedRef {
  /** Make avatar speak text */
  speak: (text: string, ssml?: string, voiceId?: string) => void;
  /** Set avatar mood/expression */
  setMood: (mood: Sentiment | string, intensity?: number) => void;
  /** Set listening state */
  setListening: (listening: boolean) => void;
  /** Set thinking state */
  setThinking: (thinking: boolean) => void;
  /** Play blendshape animation */
  playBlendshapes: (frames: BlendshapeFrame[]) => void;
  /** Send session context */
  setSessionContext: (context: Partial<AvatarSessionContext>) => void;
  /** Stop all animations */
  stopAllAnimations: () => void;
  /** Restart the ambient idle animation layers */
  playIdleAnimation: () => void;
  /** Update avatar appearance (eye, hair, skin, clothing colors) */
  setAppearance: (appearance: Record<string, string | undefined>) => void;
  /** Apply a full runtime config (appearance, visual, animation, sentiment, moods, face). */
  applyConfig: (config: Record<string, unknown>) => void;
  /** Check if avatar is ready */
  isReady: () => boolean;
  /** Get current embed mode */
  getMode: () => AvatarEmbedMode;
  /**
   * Send a typed/synthetic user turn into an active live contextual-chat
   * session (`visual.input`). Native mode only — hosts driving their own
   * text transcript/input (e.g. Camlin Companion, doc 116 CO1) use this
   * instead of the SDK's built-in chat UI or browser mic input. No-op
   * (with a console warning) in iframe mode or when no live session is
   * active.
   */
  sendText: (value: string, metadata?: Record<string, unknown>) => void;
  /**
   * True when the live visual channel is linked and its socket is open —
   * a send right now would actually reach the runtime. Native mode only.
   */
  isLiveLinked: () => boolean;
  /**
   * Unlock browser audio playback (autoplay policy) from a user-gesture
   * context and mark live prompt playback unlocked. Native live mode only
   * — no-op (with a console warning) in iframe mode.
   */
  unlockAudio: () => Promise<void>;
  /**
   * Start (or stop, if already active) a browser-mic voice turn — the
   * canonical live-mode "Listen" action — for hosts that hide the SDK's
   * own controls. Native live mode only.
   */
  startVoiceTurn: () => Promise<void>;
  /**
   * Re-run the audio/mic browser-access flow after a permission prompt
   * was surfaced via `onBrowserAccessPromptChange`. Native live mode only.
   */
  retryBrowserAccess: () => Promise<void>;
}

export interface AvatarEmbedProps {
  config: AvatarEmbedConfig;
  /** Called when avatar is ready */
  onReady?: () => void;
  /** Called when avatar starts speaking */
  onSpeakingStart?: () => void;
  /** Called when avatar stops speaking */
  onSpeakingEnd?: () => void;
  /** Called when mood changes */
  onMoodChange?: (mood: string) => void;
  /** Called when avatar receives speech input */
  onSpeechInput?: (text: string) => void;
  /** Called on error */
  onError?: (error: Error) => void;
  /** Called after default + initial appearance has been applied in native mode. */
  onAppearanceApplied?: () => void;
  /** CSS class name */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Called when mic button is clicked */
  onMicClick?: () => void;
  /**
   * Native live mode: fires when the SDK's audio/microphone browser-access
   * prompt state changes (null = cleared). Pair with
   * `config.hideAudioPermissionChip` to render host-owned permission UI.
   */
  onBrowserAccessPromptChange?: (
    prompt: { kind: 'audio' | 'microphone'; title: string; message: string } | null
  ) => void;
}

// ============================================
// Default configuration
// ============================================

const CAMLIN_TLDS = ['camlinconnect.com.au', 'camlinconnect.co.uk', 'camlinconnect.com'];
const DEFAULT_AVATAR_URL = 'https://avatar.camlinconnect.com.au';
const LOCAL_AVATAR_URL = 'http://localhost:3000';
const LOCAL_RUNTIME_HOST_URL = 'http://localhost:3002';
const LOCAL_DEV_IFRAME_CACHE_BUSTER = `dev-${Date.now()}`;

function resolveDefaultAvatarUrl(explicitUrl?: string): string {
  if (explicitUrl) {
    return explicitUrl;
  }

  if (typeof window === 'undefined') {
    return DEFAULT_AVATAR_URL;
  }

  if (window.location.hostname === 'localhost') {
    return LOCAL_AVATAR_URL;
  }

  const matchingTld = CAMLIN_TLDS.find((tld) =>
    window.location.hostname === tld || window.location.hostname.endsWith(`.${tld}`)
  );

  return matchingTld ? `https://avatar.${matchingTld}` : DEFAULT_AVATAR_URL;
}

function isLocalAvatarUrl(baseUrl: string): boolean {
  try {
    const parsed = new URL(baseUrl);
    return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

function resolveEffectiveLiveSessionKind(
  config: AvatarEmbedConfig,
  liveConfig?: AvatarHostLiveConfig,
  liveEnabled = false
): AvatarLiveSessionKind | undefined {
  return liveEnabled
    ? liveConfig?.liveSessionKind ?? config.liveSessionKind
    : config.liveSessionKind;
}

function shouldDisableSipTransport(
  config: AvatarEmbedConfig,
  liveConfig?: AvatarHostLiveConfig,
  liveEnabled = false
): boolean {
  if (!config.hideSip) {
    return false;
  }

  if (liveEnabled) {
    // `hideSip` and `handleInputExternally` are presentation/transcript-routing
    // flags. They MUST NOT force the native live host off the WebRTC/SIP
    // transport — Decision Memory and AgentLink both opt into external transcript
    // handling but still expect the canonical live/WebRTC voice path. Hosts that
    // genuinely have no voice plumbing (e.g. Visual IVR) signal that via
    // `visualMode: true` (which sets `isVisualOnly`), not via these flags.
    return false;
  }

  return true;
}

// ============================================
// AvatarEmbed Component
// ============================================

export const AvatarEmbed = forwardRef<AvatarEmbedRef, AvatarEmbedProps>(
  function AvatarEmbed(
    {
      config,
      onReady,
      onSpeakingStart,
      onSpeakingEnd,
      onMoodChange,
      onSpeechInput,
      onError,
      onAppearanceApplied,
      className,
      style,
      onMicClick,
      onBrowserAccessPromptChange
    },
    ref
  ) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const nativeAvatarRef = useRef<CamlinAvatarHandle>(null);
    const [isReady, setIsReady] = useState(false);
    const [nativeSessionContextOverride, setNativeSessionContextOverride] = useState<Partial<AvatarSessionContext> | null>(null);
    const latestSessionContextRef = useRef<AvatarEmbedCommandEnvelope | null>(null);
    const liveUnavailableKeyRef = useRef<string | null>(null);

    // Determine mode and URLs
    const mode = config.mode ?? 'iframe';
    const rawLiveConfig = useMemo(
      () => resolveRequestedLiveConfig(config),
      [config]
    );
    // `transport: 'voice-call'` = visual-only sync to an external SIP call.
    // `resolveLiveTransportConfig` normalizes the live config so the rest of
    // the component (session-context build, native props, mount key, iframe
    // URL) treats it as a NON-contextual-chat, NON-WebRTC visual session. This
    // is what makes the SDK `visual.link`-subscribe the phone's session (with
    // the existing `session-not-found` retry) instead of issuing a competing
    // contextual-chat `session.start` on the same handoffToken — the
    // duplicate-session crux. See `AvatarHostLiveConfig.transport`.
    const { isVoiceCallTransport, effectiveLiveConfig: requestedLiveConfig } = useMemo(
      () => resolveLiveTransportConfig(rawLiveConfig),
      [rawLiveConfig]
    );
    const liveAvailability = useMemo(
      () => normalizeAvatarHostLiveAvailability(requestedLiveConfig?.availability),
      [requestedLiveConfig]
    );
    const liveEnabled = Boolean(
      requestedLiveConfig?.enabled && (liveAvailability?.available ?? true)
    );
    const baseUrl = useMemo(() => {
      const defaultBaseUrl = resolveDefaultAvatarUrl(config.avatarUrl);
      return liveEnabled
        ? resolveLiveEntryUrl(defaultBaseUrl, config.avatarUrl, requestedLiveConfig?.entryUrl)
        : defaultBaseUrl;
    }, [config.avatarUrl, liveEnabled, requestedLiveConfig?.entryUrl]);
    const resolvedSessionContext = useMemo(
      () => buildResolvedSessionContext(config, requestedLiveConfig, liveEnabled),
      [config, liveEnabled, requestedLiveConfig]
    );
    const nativeResolvedSessionContext = useMemo(
      () => buildResolvedSessionContext(config, requestedLiveConfig, liveEnabled, nativeSessionContextOverride),
      [config, liveEnabled, nativeSessionContextOverride, requestedLiveConfig]
    );
    const nativeSessionContextKey = useMemo(
      () => buildSessionContextKey(nativeResolvedSessionContext),
      [nativeResolvedSessionContext]
    );
    const nativeLiveMountKey = useMemo(() => {
      const hasWebrtcDestination = Boolean(
        (requestedLiveConfig?.liveDestination ?? config.liveDestination)?.trim()
      );

      return requestedLiveConfig?.liveSessionKind === 'contextual-chat' && !hasWebrtcDestination
        ? nativeSessionContextKey
        : 'native-live-webrtc-call';
    }, [
      config.liveDestination,
      nativeSessionContextKey,
      requestedLiveConfig?.liveDestination,
      requestedLiveConfig?.liveSessionKind
    ]);
    const effectiveNativeModelUrl = useMemo(() => {
      if (!config.modelUrl || !config.avatarQuality) {
        return config.modelUrl;
      }
      const model = getModelByUrl(config.modelUrl);
      return model ? resolveModelUrlForQuality(model, config.avatarQuality) : config.modelUrl;
    }, [config.avatarQuality, config.modelUrl]);
    const runtimeConfigState = useAvatarRuntimeConfig({
      enabled: mode === 'native' && !config.initialConfig && Boolean(config.projectToken && config.avatarModelId),
      projectToken: config.projectToken,
      modelId: config.avatarModelId,
      runtimeUrl: config.runtimeUrl
    });
    const effectiveInitialConfig = config.initialConfig ?? runtimeConfigState.config ?? undefined;
    const sessionContextEnvelope = useMemo<AvatarEmbedCommandEnvelope | null>(
      () => buildSessionContextEnvelope(resolvedSessionContext, liveEnabled),
      [resolvedSessionContext, liveEnabled]
    );
    const disableSipTransport = useMemo(
      () => shouldDisableSipTransport(config, requestedLiveConfig, liveEnabled),
      [config, requestedLiveConfig, liveEnabled]
    );

    // Build iframe URL with params
    const iframeSrc = buildAvatarUrl(baseUrl, config, requestedLiveConfig, liveEnabled, resolvedSessionContext);

    // ----------------------------------------
    // Iframe communication
    // ----------------------------------------

    const postToIframe = useCallback((message: AvatarEmbedCommandEnvelope) => {
      if (mode === 'iframe' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(message, '*');
      }
    }, [mode]);

    const flushSessionContext = useCallback(() => {
      if (mode !== 'iframe' || !isReady || !latestSessionContextRef.current) {
        return;
      }

      postToIframe(latestSessionContextRef.current);
    }, [isReady, mode, postToIframe]);

    useEffect(() => {
      latestSessionContextRef.current = sessionContextEnvelope;
    }, [sessionContextEnvelope]);

    useEffect(() => {
      if (mode !== 'iframe') {
        return;
      }

      setIsReady(false);
    }, [iframeSrc, mode]);

    useEffect(() => {
      if (mode !== 'native' || !liveEnabled) {
        return;
      }

      setIsReady(false);
    }, [liveEnabled, mode, nativeLiveMountKey]);

    useEffect(() => {
      if (mode !== 'native' || liveEnabled) {
        return;
      }

      setIsReady(false);
    }, [effectiveNativeModelUrl, liveEnabled, mode]);

    useEffect(() => {
      flushSessionContext();
    }, [flushSessionContext]);

    useEffect(() => {
      if (!requestedLiveConfig?.enabled) {
        liveUnavailableKeyRef.current = null;
        return;
      }

      if (!liveAvailability || liveAvailability.available) {
        liveUnavailableKeyRef.current = null;
        return;
      }

      const unavailableKey = `${liveAvailability.reason ?? ''}:${liveAvailability.fallbackMode ?? ''}`;
      if (liveUnavailableKeyRef.current === unavailableKey) {
        return;
      }

      liveUnavailableKeyRef.current = unavailableKey;
      requestedLiveConfig.onUnavailable?.(liveAvailability);
    }, [liveAvailability, requestedLiveConfig]);

    // Handle messages from iframe
    useEffect(() => {
      if (mode !== 'iframe') return;

      const handleMessage = (event: MessageEvent) => {
        // Validate origin
        const validOrigins = [
          baseUrl,
          requestedLiveConfig?.entryUrl,
          LOCAL_AVATAR_URL,
          LOCAL_RUNTIME_HOST_URL,
          config.avatarUrl,
          DEFAULT_AVATAR_URL
        ].filter(Boolean);
        if (!validOrigins.some(origin => event.origin.includes(new URL(origin!).hostname))) {
          return;
        }

        const data = event.data;
        if (!isAvatarEmbedEnvelope(data)) return;

        switch (data.type) {
          case AVATAR_EMBED_EVENT.ready:
            setIsReady(true);
            if (config.initialAppearance && Object.keys(config.initialAppearance).length > 0) {
              setTimeout(() => {
                postToIframe({
                  type: AVATAR_EMBED_COMMAND.setAppearance,
                  payload: { appearance: config.initialAppearance! }
                });
              }, 400);
            }
            onReady?.();
            break;
          case AVATAR_EMBED_EVENT.speakingStarted:
          case AVATAR_EMBED_EVENT.speakingLegacy:
            onSpeakingStart?.();
            break;
          case AVATAR_EMBED_EVENT.speakingEnded:
            onSpeakingEnd?.();
            break;
          case AVATAR_EMBED_EVENT.moodChanged:
            onMoodChange?.(data.payload?.mood ?? 'neutral');
            break;
          case AVATAR_EMBED_EVENT.speechInput:
          case AVATAR_EMBED_EVENT.speechRecognized:
            onSpeechInput?.(data.payload?.text ?? '');
            break;
          case AVATAR_EMBED_EVENT.speakError:
            onError?.(new Error(data.payload?.error ?? data.payload?.message ?? 'Avatar error'));
            break;
        }
      };

      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, [mode, baseUrl, config.avatarUrl, onReady, onSpeakingStart, onSpeakingEnd, onMoodChange, onSpeechInput, onError, requestedLiveConfig?.entryUrl]);

    // ----------------------------------------
    // Imperative API
    // ----------------------------------------

    useImperativeHandle(ref, () => ({
      speak: (text: string, ssml?: string, voiceId?: string) => {
        // Voice-mismatch regression, 7 Aug 2026: never let a speak() travel
        // without a voice. The host's configured voice fills the gap here;
        // NativeLiveAvatar additionally pins the resolved model/persona voice
        // for native embeds whose config carries no explicit voiceId.
        const resolvedVoiceId = voiceId || config.voiceId;
        if (mode === 'iframe') {
          postToIframe({
            type: AVATAR_EMBED_COMMAND.speak,
            payload: { text, ssml, voice: resolvedVoiceId }
          });
        } else {
          // Native mode - call the CamlinAvatar ref directly
          if (nativeAvatarRef.current) {
            nativeAvatarRef.current.speak(text, { ssml, voiceId: resolvedVoiceId });
          }
        }
      },

      setMood: (mood: Sentiment | string, intensity?: number) => {
        if (mode === 'iframe') {
          postToIframe({
            type: AVATAR_EMBED_COMMAND.setMood,
            payload: { mood, intensity }
          });
        } else {
          // Native mode - call the CamlinAvatar ref directly
          if (nativeAvatarRef.current) {
            nativeAvatarRef.current.setMood(mood as Sentiment, intensity);
          }
        }
      },

      setListening: (listening: boolean) => {
        if (mode === 'iframe') {
          postToIframe({
            type: AVATAR_EMBED_COMMAND.setListening,
            payload: { listening }
          });
        } else {
          if (nativeAvatarRef.current) {
            nativeAvatarRef.current.setListening(listening);
          }
        }
      },

      setThinking: (thinking: boolean) => {
        if (mode === 'iframe') {
          postToIframe({
            type: AVATAR_EMBED_COMMAND.setThinking,
            payload: { thinking }
          });
        } else {
          if (nativeAvatarRef.current) {
            nativeAvatarRef.current.setThinking(thinking);
          }
        }
      },

      playBlendshapes: (frames: BlendshapeFrame[]) => {
        if (mode === 'iframe') {
          postToIframe({
            type: AVATAR_EMBED_COMMAND.playBlendshapes,
            payload: { frames }
          });
        } else {
          if (nativeAvatarRef.current) {
            nativeAvatarRef.current.playBlendshapes(frames);
          }
        }
      },

      setSessionContext: (context) => {
        if (mode === 'iframe') {
          const envelope = buildSessionContextEnvelope(context, liveEnabled);

          latestSessionContextRef.current = envelope ?? null;

          if (envelope && isReady) {
            postToIframe(envelope);
          }
          return;
        }

        setNativeSessionContextOverride((previous) => mergeSessionContextOverrides(previous, context));
      },

      stopAllAnimations: () => {
        if (mode === 'iframe') {
          postToIframe({ type: AVATAR_EMBED_COMMAND.stopAllAnimations });
        } else {
          if (nativeAvatarRef.current) {
            nativeAvatarRef.current.stopAllAnimations?.();
          }
        }
      },

      playIdleAnimation: () => {
        if (mode === 'iframe') {
          postToIframe({ type: AVATAR_EMBED_COMMAND.playIdleAnimation });
        } else {
          nativeAvatarRef.current?.playIdleAnimation?.();
        }
      },

      setAppearance: (appearance: Record<string, string | undefined>) => {
        if (mode === 'iframe') {
          postToIframe({
            type: AVATAR_EMBED_COMMAND.setAppearance,
            payload: { appearance }
          });
        } else {
          window.dispatchEvent(new CustomEvent('avatar:config:update', {
            detail: { appearance }
          }));
        }
      },

      applyConfig: (runtimeConfig: Record<string, unknown>) => {
        if (mode === 'iframe') {
          postToIframe({
            type: AVATAR_EMBED_COMMAND.applyConfig,
            payload: { config: runtimeConfig }
          });
        } else {
          replayAvatarRuntimeConfig(runtimeConfig);
        }
      },

      isReady: () => isReady,

      getMode: () => mode,

      sendText: (value: string, metadata?: Record<string, unknown>) => {
        if (mode === 'native') {
          nativeAvatarRef.current?.sendText?.(value, metadata);
        } else {
          console.warn('[AvatarEmbed] sendText requires mode="native" with a live contextual-chat session (not supported in iframe mode)');
        }
      },

      isLiveLinked: () => {
        if (mode === 'native') {
          return nativeAvatarRef.current?.isLiveLinked?.() ?? false;
        }
        return false;
      },

      unlockAudio: async () => {
        if (mode === 'native') {
          await nativeAvatarRef.current?.unlockAudio?.();
        } else {
          console.warn('[AvatarEmbed] unlockAudio requires mode="native" (iframe mode manages its own audio unlock)');
        }
      },

      startVoiceTurn: async () => {
        if (mode === 'native') {
          await nativeAvatarRef.current?.startVoiceTurn?.();
        } else {
          console.warn('[AvatarEmbed] startVoiceTurn requires mode="native" with a live session');
        }
      },

      retryBrowserAccess: async () => {
        if (mode === 'native') {
          await nativeAvatarRef.current?.retryBrowserAccess?.();
        } else {
          console.warn('[AvatarEmbed] retryBrowserAccess requires mode="native"');
        }
      }
    }), [mode, postToIframe, isReady, liveEnabled]);

    // ----------------------------------------
    // Render
    // ----------------------------------------

    if (mode === 'native') {
      if (liveEnabled) {
        return (
          <NativeLiveAvatar
            key={nativeLiveMountKey}
            ref={nativeAvatarRef}
            activeListen={config.activeListen}
            holdSpeechUntilUnlock={config.holdSpeechUntilUnlock}
            cameraZoom={config.cameraZoom}
            className={className}
            diagramId={config.diagramId}
            handoffToken={config.handoffToken}
            isVisualOnly={config.visualMode || isVoiceCallTransport}
            liveDestination={isVoiceCallTransport ? undefined : (requestedLiveConfig?.liveDestination ?? config.liveDestination)}
            modelUrl={effectiveNativeModelUrl}
            noSip={disableSipTransport}
            onError={onError}
            onMicClick={onMicClick}
            onMoodChange={onMoodChange}
            onReady={() => {
              setIsReady(true);
              onReady?.();
            }}
            onSpeakingEnd={onSpeakingEnd}
            onSpeakingStart={onSpeakingStart}
            onSpeechInput={onSpeechInput}
            projectToken={config.projectToken}
            requestedAvatarModelId={config.avatarModelId}
            requestedLiveSessionKind={requestedLiveConfig?.liveSessionKind}
            requestedLiveVoiceId={config.voiceId}
            runtimeUrl={config.runtimeUrl}
            sessionContext={nativeResolvedSessionContext}
            hideButtons={config.hideButtons}
            hideHealthBadge={config.hideHealthBadge}
            hideAudioPermissionChip={config.hideAudioPermissionChip}
            onBrowserAccessPromptChange={onBrowserAccessPromptChange}
            preloadModel={config.preloadModel}
            sipWebSocketUrl={requestedLiveConfig?.sipWebSocketUrl ?? config.sipWebSocketUrl}
            style={style}
            suppressEmbeddedLiveCopy={requestedLiveConfig?.suppressEmbeddedLiveCopy}
            handleInputExternally={requestedLiveConfig?.handleInputExternally}
            autoOpenChannel={requestedLiveConfig?.autoOpenChannel}
            ttsUrl={config.ttsUrl}
            useEmbeddedTextures={config.useEmbeddedTextures}
            voiceUrl={config.voiceUrl}
            initialAppearance={config.initialAppearance}
            initialConfig={effectiveInitialConfig ?? undefined}
            onAppearanceApplied={onAppearanceApplied}
          />
        );
      }

      // Native mode - use CamlinAvatar with full 3D
      return (
        <CamlinAvatar
          ref={nativeAvatarRef}
          config={{
            mode: 'full',
            showChat: !config.hideChat,
            showButtons: !config.hideButtons,
            enableSentiment: config.enableSentiment,
            cameraZoom: config.cameraZoom,
            modelUrl: effectiveNativeModelUrl,
            ttsUrl: config.ttsUrl,
            projectToken: resolvedSessionContext?.projectToken ?? config.projectToken,
            handoffToken: resolvedSessionContext?.handoffToken ?? config.handoffToken,
            diagramId: resolvedSessionContext?.diagramId ?? config.diagramId,
            showAvatarSwitcher: config.showAvatarSwitcher,
            avatarModels: config.avatarModels,
            showMicButton: config.showMicButton,
            useEmbeddedTextures: config.useEmbeddedTextures,
            initialAppearance: config.initialAppearance,
            initialConfig: effectiveInitialConfig,
            isLiveMode: liveEnabled,
            enableCanvasCapture: config.enableCanvasCapture
          }}
          onReady={() => {
            setIsReady(true);
            onReady?.();
          }}
          onAppearanceApplied={onAppearanceApplied}
          onSpeakStart={onSpeakingStart}
          onSpeakComplete={onSpeakingEnd}
          onMoodChange={(mood) => onMoodChange?.(mood)}
          onError={onError}
          onMicClick={onMicClick}
          className={className}
          style={style}
        />
      );
    }

    // Iframe mode (default)
    return (
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        className={className}
        style={{
          border: 'none',
          width: '100%',
          height: '100%',
          ...style
        }}
        title="Camlin Avatar"
        allow="microphone; autoplay; camera; clipboard-write"
      />
    );
  }
);

// ============================================
// Helper Functions
// ============================================

function resolveRequestedLiveConfig(config: AvatarEmbedConfig): AvatarHostLiveConfig | undefined {
  if (config.live) {
    return config.live;
  }

  const legacyLiveSessionKind =
    config.liveSessionKind ?? (config.contextualLive ? 'contextual-chat' : undefined);

  if (!legacyLiveSessionKind) {
    return undefined;
  }

  return {
    enabled: true,
    liveSessionKind: legacyLiveSessionKind
  };
}

function buildLiveEntryUrl(baseUrl: string): string {
  try {
    const resolved = new URL(baseUrl);
    const normalizedPathname = resolved.pathname.replace(/\/+$/, '');
    resolved.pathname = normalizedPathname.endsWith('/live')
      ? normalizedPathname
      : `${normalizedPathname || ''}/live`;
    return resolved.toString();
  } catch {
    return baseUrl;
  }
}

function resolveLiveEntryUrl(
  defaultBaseUrl: string,
  explicitAvatarUrl?: string,
  explicitEntryUrl?: string
): string {
  if (explicitEntryUrl) {
    return explicitEntryUrl;
  }

  if (
    (typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname))
    || isLocalAvatarUrl(explicitAvatarUrl ?? defaultBaseUrl)
  ) {
    return `${LOCAL_RUNTIME_HOST_URL}/live`;
  }

  return buildLiveEntryUrl(defaultBaseUrl);
}

function hasSessionContext(context?: Partial<AvatarSessionContext> | null): boolean {
  if (!context) {
    return false;
  }

  return Object.entries(context).some(([key, value]) => {
    if (key === 'metadata') {
      return Boolean(
        value
        && typeof value === 'object'
        && Object.keys(value as Record<string, unknown>).length > 0
      );
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return value !== undefined && value !== null;
  });
}

function readSessionContextMetadata(context?: Partial<AvatarSessionContext> | null): Record<string, unknown> {
  if (!context?.metadata || typeof context.metadata !== 'object') {
    return {};
  }

  return context.metadata as Record<string, unknown>;
}

function mergeSessionContextOverrides(
  baseContext: Partial<AvatarSessionContext> | null | undefined,
  nextContext: Partial<AvatarSessionContext>
): Partial<AvatarSessionContext> {
  const mergedMetadata = {
    ...readSessionContextMetadata(baseContext),
    ...readSessionContextMetadata(nextContext)
  };

  return {
    ...(baseContext ?? {}),
    ...nextContext,
    metadata: Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined
  };
}

function buildSessionContextKey(
  context: Partial<AvatarSessionContext> | AvatarSessionContext | null
): string {
  if (!context) {
    return 'null';
  }

  const metadata = readSessionContextMetadata(context);

  return JSON.stringify({
    projectToken: context.projectToken ?? null,
    handoffToken: context.handoffToken ?? null,
    diagramId: context.diagramId ?? null,
    sessionId: context.sessionId ?? null,
    userId: context.userId ?? null,
    tenantId: context.tenantId ?? null,
    metadata: {
      liveMode: metadata.liveMode ?? null,
      liveSessionKind: metadata.liveSessionKind ?? null,
      contextualLive: metadata.contextualLive ?? null
    }
  });
}

function buildResolvedSessionContext(
  config: AvatarEmbedConfig,
  liveConfig: AvatarHostLiveConfig | undefined,
  liveEnabled: boolean,
  overrideContext?: Partial<AvatarSessionContext> | null
): AvatarSessionContext | Partial<AvatarSessionContext> | null {
  const baseSessionContext = config.sessionContext ?? {};
  const liveSessionContext = liveConfig?.sessionContext ?? {};
  const sessionOverride = overrideContext ?? {};
  const mergedMetadata = {
    ...readSessionContextMetadata(baseSessionContext),
    ...readSessionContextMetadata(liveSessionContext),
    ...readSessionContextMetadata(sessionOverride)
  };

  const mergedContext: Partial<AvatarSessionContext> = {
    ...baseSessionContext,
    ...liveSessionContext,
    ...sessionOverride,
    projectToken: sessionOverride.projectToken
      ?? config.projectToken
      ?? liveSessionContext.projectToken
      ?? baseSessionContext.projectToken,
    handoffToken: sessionOverride.handoffToken
      ?? config.handoffToken
      ?? liveSessionContext.handoffToken
      ?? baseSessionContext.handoffToken,
    diagramId: sessionOverride.diagramId
      ?? config.diagramId
      ?? liveSessionContext.diagramId
      ?? baseSessionContext.diagramId,
    sessionId: sessionOverride.sessionId
      ?? liveSessionContext.sessionId
      ?? baseSessionContext.sessionId,
    userId: sessionOverride.userId
      ?? liveSessionContext.userId
      ?? baseSessionContext.userId,
    metadata: Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined
  };

  if (!liveEnabled || !liveConfig) {
    return hasSessionContext(mergedContext) ? mergedContext : null;
  }

  if (liveConfig.liveSessionKind === 'contextual-chat') {
    return createHostContextualChatSessionContext({
      sessionContext: mergedContext,
      hostContext: liveConfig.contextualChat?.hostContext,
      contextualChat: liveConfig.contextualChat
    });
  }

  return createLiveSessionContext({
    ...mergedContext,
    metadata: {
      ...(mergedContext.metadata ?? {}),
      liveMode: true,
      liveSessionKind: liveConfig.liveSessionKind
    }
  }, mergedContext.diagramId);
}

function buildSessionContextEnvelope(
  context: Partial<AvatarSessionContext> | AvatarSessionContext | null | undefined,
  liveEnabled: boolean
): AvatarEmbedCommandEnvelope | null {
  if (!hasSessionContext(context)) {
    return null;
  }

  return {
    type: AVATAR_EMBED_COMMAND.sessionContext,
    payload: {
      ...context,
      ...(liveEnabled ? { liveMode: true, mode: 'live' as const } : {})
    }
  };
}

function buildAvatarUrl(
  baseUrl: string,
  config: AvatarEmbedConfig,
  liveConfig?: AvatarHostLiveConfig,
  liveEnabled = false,
  resolvedSessionContext?: Partial<AvatarSessionContext> | AvatarSessionContext | null
): string {
  const params = new URLSearchParams();
  const projectToken = resolvedSessionContext?.projectToken ?? config.projectToken;
  const handoffToken = resolvedSessionContext?.handoffToken ?? config.handoffToken;
  const diagramId = resolvedSessionContext?.diagramId ?? config.diagramId;
  const sessionId = resolvedSessionContext?.sessionId;
  const userId = resolvedSessionContext?.userId;
  const liveSessionKind = resolveEffectiveLiveSessionKind(config, liveConfig, liveEnabled);
  const disableSipTransport = shouldDisableSipTransport(config, liveConfig, liveEnabled);

  if (projectToken) {
    params.set('pt', projectToken);
  }
  if (handoffToken) {
    params.set('ht', handoffToken);
  }
  if (diagramId) {
    params.set('did', diagramId);
  }
  if (sessionId) {
    params.set('sessionId', sessionId);
  }
  if (userId) {
    params.set('userId', userId);
  }
  if (config.hideChat) {
    params.set('nochat', 'true');
  }
  if (config.hideButtons) {
    params.set('nobuttons', 'true');
  }
  if (disableSipTransport) {
    params.set('nosip', 'true');
  }
  if (config.enableSentiment) {
    params.set('sentiment', 'true');
  }
  if (config.builderMode) {
    params.set('builder', 'true');
  }
  if (config.cameraZoom !== undefined) {
    params.set('zoom', String(config.cameraZoom));
  }
  if (config.voiceId) {
    params.set('voice', config.voiceId);
  }
  if (config.avatarModelId) {
    params.set('avatarModelId', config.avatarModelId);
  }
  if (config.visualMode || liveConfig?.transport === 'voice-call') {
    params.set('visual', 'true');
  }
  if (config.activeListen) {
    params.set('activelisten', 'true');
  }
  if (liveEnabled) {
    params.set('mode', 'live');
  }
  if (liveSessionKind) {
    params.set('liveSessionKind', liveSessionKind);
  }
  const liveDestination = liveConfig?.liveDestination ?? config.liveDestination;
  if (liveDestination) {
    params.set('liveDestination', liveDestination);
  }
  const sipWebSocketUrl = liveConfig?.sipWebSocketUrl ?? config.sipWebSocketUrl;
  if (sipWebSocketUrl) {
    params.set('sipWebSocketUrl', sipWebSocketUrl);
  }
  if (
    (liveEnabled && liveSessionKind === 'contextual-chat')
    || config.contextualLive
    || config.liveSessionKind === 'contextual-chat'
  ) {
    params.set('contextualLive', 'true');
  }
  if (config.ttsUrl) {
    params.set('ttsUrl', config.ttsUrl);
  }
  if (isLocalAvatarUrl(baseUrl)) {
    // Force a fresh local iframe document after dev-server restarts so Vite
    // doesn't reuse stale optimized dependency URLs inside the embed.
    params.set('_embedv', LOCAL_DEV_IFRAME_CACHE_BUSTER);
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

// Export default
export default AvatarEmbed;
